/**
 * Fetches a contract's rendered label print jobs from the backend.
 *
 * Split from `domain/labelPrinting.ts` so the domain module stays free of the
 * application's API shape: the domain knows how to talk to the workstation
 * service, this knows how to ask our own backend.
 */

import type {
    LabelJobsResult,
    LabelPrinterProvider,
    LabelPrintJob,
} from '../domain/labelPrinting';

interface LabelJobsResponse {
    data?: { jobs?: LabelPrintJob[] };
    code?: string;
}

/**
 * Ask the backend to build the print jobs for a contract's object labels.
 *
 * The **installation id** identifies this workstation; the backend derives the cash
 * point — and therefore the printer queue — from the pairing binding. The page does
 * not choose it, because a page-supplied cash point would let any caller print to
 * any shop's printer.
 *
 * Never throws: a transport failure is reported as an unbuilt result, because a
 * failed label fetch must not be able to break the caller — at contract creation
 * it runs after the contract is already saved.
 */
export async function loadContractLabelJobs(
    apiBaseUrl: string,
    authHeaders: Record<string, string>,
    contractNr: number,
    installationId: string,
    // `printerId` is optional because a cross-provider rebuild has none to give: the id it held
    // named the device that just became unavailable, and the service approves the replacement
    // itself rather than taking a caller's word for it.
    resolved?: { provider: LabelPrinterProvider; printerId?: string } | null,
): Promise<LabelJobsResult> {
    let response: Response;

    // The provider comes from the device the workstation service resolved, not from the
    // cashpoint's configured queue name. Only the workstation can see which printer is
    // actually plugged into it; the central configuration is a shop-wide row that a printer
    // swap or a queue rename silently invalidates.
    const providerQuery =
        resolved == null ? '' : `&provider=${encodeURIComponent(resolved.provider)}`;

    try {
        response = await fetch(
            `${apiBaseUrl}/labels/contract/${contractNr}` +
                `?installationId=${encodeURIComponent(installationId)}${providerQuery}`,
            { method: 'GET', headers: authHeaders },
        );
    } catch {
        return { ok: false };
    }

    let body: LabelJobsResponse = {};

    try {
        body = (await response.json()) as LabelJobsResponse;
    } catch {
        // A non-JSON body is still an answer; the status decides the outcome.
    }

    if (!response.ok) {
        return { ok: false, code: body.code };
    }

    // Each job carries the resolved device back to the service, which refuses anything but
    // the printer it approved for this workstation.
    const jobs = (body.data?.jobs ?? []).map(job => ({
        ...job,
        printerId: resolved?.printerId ?? null,
    }));

    return { ok: true, jobs };
}
